'use strict'
import React from 'react'
import { FieldError, RegisterOptions } from 'react-hook-form'
import { isNotNil, keys, uniq } from 'ramda'
import { AnimatePresence, motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { capitalCase } from '@unimpaired/utils'

type Props = {
  error?: FieldError | any
  fieldValue: string | number
  validators: RegisterOptions
  className?: string
  format?: (value: any) => any
}

export const getErrorMessage = (
  fieldName: string,
  error?: FieldError | any,
) => {
  const name = capitalCase(fieldName)
  if (!error) return ''
  if (error?.message) return error.message
  if (error.type === 'required') return `${name} is required`
  if (error.type === 'min') return `${name} is too low`
  if (error.type === 'max') return `${name} is too high`
  if (error.type === 'minLength') return `${name} is too short`
  if (error.type === 'maxLength') return `${name} is too long`
  if (error.type === 'pattern') return `${name} is invalid`
  return `${name} is invalid`
}

export const InputError = ({
  error,
  fieldValue,
  validators,
  className,
  format = (value: any) => value
}: Props) => {
  const { minLength, maxLength, max, min } = validators

  const hasError = (validatorType: keyof RegisterOptions) => {
    const hasError = error && error.type && error.type === validatorType
    if (hasError) return true
    return false
  }

  const message = (type: keyof RegisterOptions) => {
    if (type === 'required') return 'Fill out this field'

    if (!fieldValue) return

    if (type === 'pattern')
      // @ts-ignore
      return validators.pattern?.message || 'invalid input'

    if (type === 'min' && min)
      return `Enter a number greater than ${format(Number(min) - 1)}`

    if (type === 'max' && max) return `Enter a number less than ${format(Number(max))}`

    if (type === 'minLength' && minLength)
      return `Enter ${
        Number(minLength || 0) - fieldValue?.toString().length
      } more characters`

    if (type === 'maxLength' && maxLength)
      return `Enter ${
        fieldValue?.toString().length - Number(maxLength || 0)
      } less characters`

    if (type === 'validate') return error.message
  }

  // form errors are delivered by the form
  // they are generally only delivered on submit when validation
  // the whole form is run (which is expensive)
  const validatorList = keys(validators).filter(hasError).map(message)

  // input errors are run on every change, which is faster
  const validatorFunctions = Object.entries(validators['validate'] || [])
    .map(([message, validator], i) =>
      error && error.type === message && !validator(fieldValue)
        ? message
        : null,
    )
    .filter((e) => isNotNil(e))

  const allErrors = uniq([...validatorList, ...validatorFunctions])

  return (
    <AnimatePresence>
      {/* {error && (
        <motion.p
          initial={{ opacity: 0, translateY: '5px' }}
          animate={{ opacity: 1, translateY: 0 }}
          exit={{ opacity: 0 }}
          className={twMerge(
            'text-sm text-red-400 overflow-hidden',
            className,
          )}>
          {error.type && message(error.type)}
        </motion.p>
      )} */}
      {allErrors.slice(0, 1).map((value, i) => (
        <motion.p
          initial={{ opacity: 0, translateY: '5px' }}
          animate={{ opacity: 1, translateY: 0 }}
          exit={{ opacity: 0 }}
          className={twMerge('text-sm text-red-400 overflow-hidden', className)}
          key={`${i}`}>
          {value}
        </motion.p>
      ))}
    </AnimatePresence>
  )
}
